import { renderToString } from 'react-dom/server';
import React, { ReactElement, ReactNode, isValidElement } from 'react';
import { type FAQ } from '@/components/atoms/SchemaStructuredData/SchemaGeneration/structuredData';
import Question, { QuestionProps } from './Question';
import Answer, { AnswerProps } from './Answer';
import { match } from '@/helpers/match';

type FAQMap = {
	[key: string]: { question: string; answer: string };
};

export const getStructuredFAQData = (children: ReactNode): FAQ => {
	const faqs: FAQMap = {};

	const findQuestionAndAnswer = (child: ReactNode) => {
		match(
			[
				isQuestion(child),
				() => {
					const qProps = (child as ReactElement<QuestionProps>).props;
					if (!faqs[qProps.panelId]) {
						faqs[qProps.panelId] = { question: '', answer: '' };
					}

					// To properly add schema we need a nested ternary to check for
					// React elements or plain text strings coming in (Answers will
					// always be React elements)
					faqs[qProps.panelId].question = isValidElement(qProps.children)
						? renderToString(qProps.children)
						: typeof qProps.children === 'string'
						? qProps.children
						: '';
				},
			],
			[
				isAnswer(child),
				() => {
					const aProps = (child as ReactElement<AnswerProps>).props;
					if (!faqs[aProps.panelId]) {
						faqs[aProps.panelId] = { question: '', answer: '' };
					}
					faqs[aProps.panelId].answer = isValidElement(aProps.children)
						? renderToString(aProps.children)
						: '';
				},
			],
			[
				hasChildren(child),
				() => {
					React.Children.forEach(
						(child as ReactElement<{ children: ReactNode }>).props.children,
						findQuestionAndAnswer,
					);
				},
			],
			// If it's not a question, or answer, and doesn't have children, ignore it
			[true, () => {}],
		);
	};

	// Kick off the recursion from the top level
	React.Children.forEach(children, findQuestionAndAnswer);

	// Convert the question/answer objects into FAQ items
	const faq: FAQ = Array.from(Object.values(faqs));

	return faq;
};

const isQuestion = (child: ReactNode): child is ReactElement<QuestionProps> => {
	return isValidElement(child) && child.type === Question;
};

const isAnswer = (child: ReactNode): child is ReactElement<AnswerProps> => {
	return isValidElement(child) && child.type === Answer;
};

const hasChildren = (child: ReactNode): boolean => {
	return (
		isValidElement(child) &&
		typeof child === 'object' &&
		typeof child.props === 'object' &&
		child.props !== null &&
		'children' in child.props &&
		React.Children.count(child.props.children) > 0
	);
};
